import { z } from "zod";

// Bucket-4 request schemas for /api/admin/content/*. The full create/update
// schemas (contentAssetCreateSchema / contentAssetUpdateSchema) already live
// in types/domain.ts and re-export through @colab/shared — only the new
// path-specific shapes land here.

// Body of PATCH /api/admin/content/assets/:id/archived — toggle the
// archived_at column on or off. One endpoint drives both directions of the
// flip; the optimistic hook (Pattern C) reads `archived` and patches
// `archived_at` locally before the server confirms.
export const contentArchiveSchema = z.object({
  archived: z.boolean(),
});
export type ContentArchiveInput = z.infer<typeof contentArchiveSchema>;

// Body of PATCH /api/admin/content/assets/:id/parent — move an asset (or a
// folder) underneath a different parent, or to the root by passing null.
// Slimmer than the legacy contentAssetMoveSchema in types/domain.ts which
// also carried `id` (now sourced from the URL path).
export const contentMoveSchema = z.object({
  parent_asset_id: z
    .union([z.literal(""), z.string().uuid()])
    .nullable()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type ContentMoveInput = z.infer<typeof contentMoveSchema>;

// Body of POST /api/admin/content/upload-init — request a one-time signed
// upload URL ahead of a direct-to-Storage PUT from the SPA. The worker mints
// a `${Date.now()}-${safeName}` key against the `content` bucket and returns
// `{ storageKey, token }`; the SPA uses `uploadToSignedUrl` to push the file
// without the service-role key.
//
// `size` is advisory — Supabase does not enforce a per-token size cap, so a
// future server-side limit will need its own gate. Kept here so a future
// `max_size` constraint can land without changing the call sites.
export const contentUploadInitSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  content_type: z.string().trim().min(1).max(255).optional(),
  size: z.number().int().nonnegative().optional(),
});
export type ContentUploadInitInput = z.infer<typeof contentUploadInitSchema>;
